import { useCallback, useContext, useMemo, useRef } from "react";
import { useIsMutating, useMutation } from "@tanstack/react-query";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import {
    updateBillExtractionDetails,
    updateInvoiceExtractionDetails,
} from "@api/financialDocument.api";
import { useCounterpartiesQuery } from "@api/counterparty.api";
import { DirectorySort } from "@common/constants/counterparty";
import { RegisteredCounterpartyIncludeScope } from "@common/helpers/counterparties";
import { useScope } from "@state/scopeContext.lib";
import { matchExtractedCounterparty } from "./draftDetails/matchExtractedCounterparty";
import { AccrualImporterContext } from "./accrualImporter.context";
import {
    accrualDocumentBeingProcessed,
    getAccrualExtraction,
    resolveAccrualRowExtraction,
} from "./accrualImporter.helpers";
import {
    EMPTY_PARTY_HINTS,
    getPartyHintsFromOriginalExtraction,
} from "./getPartyHintsFromOriginalExtraction";
import {
    buildDocumentWithCounterparty,
    getAccrualCounterpartyMutationKey,
    getExtractionOrderIndex,
} from "./useAccrualImporterCounterpartyEditor.helpers";

interface UseAccrualImporterCounterpartyEditorParams {
    financialDocument: FinancialDocumentExtended;
    documentRowId: string;
    extractionId?: string;
}

export function useAccrualImporterCounterpartyEditor({
    financialDocument,
    documentRowId,
    extractionId,
}: UseAccrualImporterCounterpartyEditorParams) {
    const { mode, updateDocumentRow, revalidateDocument } = useContext(
        AccrualImporterContext,
    );
    const { workspaceId } = useScope();
    const financialDocumentRef = useRef(financialDocument);
    financialDocumentRef.current = financialDocument;

    const extraction = useMemo(
        () =>
            resolveAccrualRowExtraction(financialDocument, mode, extractionId),
        [financialDocument, mode, extractionId],
    );
    const selectedCounterpartyId = extraction?.counterpartyId ?? null;
    const pendingCounterpartyName = extraction?.counterpartyName ?? null;

    const partyHints = useMemo(() => {
        if (!extraction) {
            return EMPTY_PARTY_HINTS;
        }

        const orderIndex = getExtractionOrderIndex({
            financialDocument,
            extractionId: extraction.id,
            mode,
        });
        if (orderIndex == null) {
            return EMPTY_PARTY_HINTS;
        }

        const containerExtraction = getAccrualExtraction(financialDocument);
        return getPartyHintsFromOriginalExtraction({
            originalExtraction: containerExtraction?.originalExtraction,
            orderIndex,
            mode,
        });
    }, [extraction, financialDocument, mode]);

    const counterpartiesQuery = useCounterpartiesQuery({
        sort: DirectorySort.NAME_ASC,
        includeRegistered:
            selectedCounterpartyId != null
                ? RegisteredCounterpartyIncludeScope.ALL
                : RegisteredCounterpartyIncludeScope.USED,
    });

    const counterparties = useMemo(
        () =>
            (counterpartiesQuery.data?.data ?? []).map(
                (row) => row.counterparty,
            ),
        [counterpartiesQuery.data],
    );

    const extractedName = pendingCounterpartyName?.trim() ?? "";
    const nameMatchQuery = useCounterpartiesQuery(
        {
            sort: DirectorySort.NAME_ASC,
            search: extractedName,
            includeRegistered: RegisteredCounterpartyIncludeScope.ALL,
        },
        {
            enabled: selectedCounterpartyId == null && extractedName !== "",
        },
    );
    const matchedCounterparty = useMemo(() => {
        if (
            selectedCounterpartyId != null ||
            !workspaceId ||
            extractedName === ""
        ) {
            return null;
        }
        return matchExtractedCounterparty({
            name: extractedName,
            workspaceId,
            counterparties: (nameMatchQuery.data?.data ?? []).map(
                (row) => row.counterparty,
            ),
        });
    }, [
        extractedName,
        nameMatchQuery.data,
        selectedCounterpartyId,
        workspaceId,
    ]);

    const selectedCounterparty = useMemo(
        () =>
            selectedCounterpartyId != null
                ? counterparties.find(
                      (counterparty) =>
                          counterparty.id === selectedCounterpartyId,
                  )
                : undefined,
        [counterparties, selectedCounterpartyId],
    );

    const isProcessing = accrualDocumentBeingProcessed(financialDocument);
    const mutationKey = getAccrualCounterpartyMutationKey(
        financialDocument.id,
        extractionId,
    );

    const { mutate: persistCounterpartyDetails } = useMutation({
        mutationKey,
        mutationFn: (params: {
            counterpartyId?: string | null;
            counterpartyName?: string | null;
            acceptSuggestion?: true;
        }) => {
            const currentDocument = financialDocumentRef.current;
            const primaryExtraction = resolveAccrualRowExtraction(
                currentDocument,
                mode,
                extractionId,
            );
            if (!primaryExtraction) {
                throw new Error("Missing accrual extraction");
            }

            const persistedExtractionId = primaryExtraction.id;
            const body = {
                financialDocumentId: currentDocument.id,
                ...params,
            };

            if (mode === "bills") {
                return updateBillExtractionDetails({
                    ...body,
                    billExtractionId: persistedExtractionId,
                });
            }

            return updateInvoiceExtractionDetails({
                ...body,
                invoiceExtractionId: persistedExtractionId,
            });
        },
        onMutate: (params) => {
            const previousDocument = financialDocumentRef.current;
            const currentExtraction = resolveAccrualRowExtraction(
                previousDocument,
                mode,
                extractionId,
            );

            updateDocumentRow(documentRowId, {
                financialDocument: buildDocumentWithCounterparty({
                    financialDocument: previousDocument,
                    counterpartyId:
                        params.counterpartyId !== undefined
                            ? params.counterpartyId
                            : (currentExtraction?.counterpartyId ?? null),
                    counterpartyName:
                        params.counterpartyName !== undefined
                            ? params.counterpartyName
                            : (currentExtraction?.counterpartyName ?? null),
                    mode,
                    extractionId,
                }),
            });

            return previousDocument;
        },
        onSuccess: () => {
            revalidateDocument({
                financialDocumentId: financialDocumentRef.current.id,
            });
        },
        onError: (_error, _params, previousDocument) => {
            if (previousDocument) {
                updateDocumentRow(documentRowId, {
                    financialDocument: previousDocument,
                });
                revalidateDocument({
                    financialDocumentId: previousDocument.id,
                });
            }
        },
    });

    const isPending =
        useIsMutating({
            mutationKey,
        }) > 0;

    const onCounterpartyChanged = useCallback(
        (counterpartyId: string | null) => {
            if (isProcessing || isPending) {
                return;
            }

            if (counterpartyId === selectedCounterpartyId) {
                return;
            }

            if (counterpartyId === null) {
                persistCounterpartyDetails({
                    counterpartyId: null,
                    counterpartyName: null,
                });
                return;
            }

            persistCounterpartyDetails({
                counterpartyId,
                counterpartyName: null,
            });
        },
        [
            isPending,
            isProcessing,
            persistCounterpartyDetails,
            selectedCounterpartyId,
        ],
    );

    const onConfirmMatchedCounterparty = useCallback(
        (counterpartyId: string, counterpartyName: string) => {
            if (isProcessing || isPending) {
                return;
            }
            persistCounterpartyDetails({
                counterpartyId,
                counterpartyName,
                acceptSuggestion: true,
            });
        },
        [isPending, isProcessing, persistCounterpartyDetails],
    );

    const onCounterpartyCreated = useCallback(
        (counterpartyId: string) => {
            if (isProcessing || isPending) {
                return;
            }

            persistCounterpartyDetails({
                counterpartyId,
                counterpartyName: null,
            });
        },
        [isPending, isProcessing, persistCounterpartyDetails],
    );

    const disabledReason = useMemo(() => {
        if (isProcessing) {
            return "Document is still processing";
        }
        if (isPending) {
            return "Saving...";
        }
        return undefined;
    }, [isProcessing, isPending]);

    const canCreateCounterparty =
        selectedCounterpartyId == null &&
        Boolean(pendingCounterpartyName?.trim());

    return {
        counterparties,
        selectedCounterparty,
        selectedCounterpartyId,
        pendingCounterpartyName,
        matchedCounterparty,
        partyHints,
        canCreateCounterparty,
        onCounterpartyChanged,
        onCounterpartyCreated,
        onConfirmMatchedCounterparty,
        disabledReason,
    };
}
